import { useState } from "react"
import { useParams, Link } from "react-router-dom"
import { useQuery, useMutation } from "@tanstack/react-query"
import {
  getPublicQuote,
  acceptPublicQuote,
  rejectPublicQuote,
  publicQuotePdfUrl,
  type PublicQuoteResponse,
} from "@/services/publicQuote"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Checkbox } from "@/components/ui/checkbox"
import { Textarea } from "@/components/ui/textarea"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import * as O from "fp-ts/Option"
import { pipe } from "fp-ts/function"
import { serverDetail } from "@/services/public-fetch"
import { useToast } from "@/hooks/use-toast"
import { FileText, Download, CheckCircle2, XCircle, ClipboardList, AlertTriangle, Info } from "lucide-react"
import { formatQuantity } from "@/lib/money"
import { formatIsoDateFr } from "@/lib/organisation-day"

function formatMoney(value: string | number, currency: string): string {
  const n = typeof value === "string" ? parseFloat(value) : value
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: currency || "EUR",
  }).format(n)
}

const LONG_DAY: Intl.DateTimeFormatOptions = { day: "2-digit", month: "long", year: "numeric" }

/** Date calendaire du devis (émission, validité), telle qu'écrite. */
function formatDate(day: string): string {
  return formatIsoDateFr(day, LONG_DAY)
}

/**
 * Acceptation ou refus : instant au fuseau du navigateur.
 * L'API publique ne sert pas celui de l'organisme.
 */
function formatInstantDate(iso: string): string {
  return new Date(iso).toLocaleDateString("fr-FR", LONG_DAY)
}

function statusBadge(code: string, label: string | null) {
  const map: Record<string, { variant: "default" | "secondary" | "destructive" | "outline"; text: string }> = {
    draft: { variant: "secondary", text: "Brouillon" },
    sent: { variant: "default", text: "Envoyé" },
    accepted: { variant: "default", text: "Accepté" },
    rejected: { variant: "destructive", text: "Refusé" },
    expired: { variant: "outline", text: "Expiré" },
  }
  const def = map[code] ?? { variant: "outline" as const, text: label || code }
  return <Badge variant={def.variant} data-test="quote-status-badge">{def.text}</Badge>
}

export default function PublicQuotePage() {
  const { token } = useParams<{ token: string }>()
  const { toast } = useToast()
  const [acceptOpen, setAcceptOpen] = useState(false)
  const [rejectOpen, setRejectOpen] = useState(false)

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["public-quote", token],
    queryFn: () => getPublicQuote(token!),
    enabled: !!token,
    retry: false,
  })

  if (!token) {
    return <PublicShell><ErrorState message="Token manquant" /></PublicShell>
  }

  if (isLoading) {
    return (
      <PublicShell>
        <div className="p-8 text-center text-muted-foreground">Chargement du devis…</div>
      </PublicShell>
    )
  }

  if (isError || !data) {
    return (
      <PublicShell>
        <ErrorState message={pipe(serverDetail(error), O.getOrElse(() => "Ce lien est invalide, révoqué ou expiré."))} />
      </PublicShell>
    )
  }

  const canAct = !data.is_expired && data.status_code === "sent"
  const isAccepted = data.status_code === "accepted"
  const isRejected = data.status_code === "rejected"

  return (
    <PublicShell tenantName={data.tenant.name} tenantLogo={data.tenant.logo_url}>
      <div className="mx-auto max-w-3xl space-y-6 p-4 md:p-8" data-test="public-quote-page">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold" data-test="quote-number">
              Devis {data.quote_number}
            </h1>
            {data.reference && (
              <p className="text-sm text-muted-foreground">Référence : {data.reference}</p>
            )}
          </div>
          {statusBadge(data.status_code, data.status_label)}
        </div>

        {data.is_expired && !isAccepted && !isRejected && (
          <div className="flex items-center gap-2 rounded-md border border-orange-200 bg-orange-50 px-4 py-3 text-orange-900">
            <AlertTriangle className="h-5 w-5" />
            <span>Ce devis a expiré le {formatDate(data.validity_date)}. Contactez votre interlocuteur pour un nouveau devis.</span>
          </div>
        )}

        {isAccepted && (
          <div className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-emerald-900" data-test="quote-accepted-banner">
            <div className="flex items-center gap-2 font-medium">
              <CheckCircle2 className="h-5 w-5" />
              Devis accepté{data.accepted_at && ` le ${formatInstantDate(data.accepted_at)}`}
              {data.accepted_by_name && ` par ${data.accepted_by_name}`}
            </div>
            <p className="mt-2 text-sm text-emerald-800" data-test="quote-signature-confirmation">
              Votre signature a été enregistrée. Un email de confirmation avec le devis signé vous sera envoyé sous quelques minutes.
            </p>
          </div>
        )}
        {isRejected && (
          <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-rose-900" data-test="quote-rejected-banner">
            <div className="flex items-center gap-2 font-medium">
              <XCircle className="h-5 w-5" />
              Devis refusé{data.rejected_at && ` le ${formatInstantDate(data.rejected_at)}`}
              {data.rejected_by_name && ` par ${data.rejected_by_name}`}
            </div>
          </div>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Émetteur et destinataire</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 text-sm md:grid-cols-2">
            <div>
              <div className="font-medium">Émetteur</div>
              <div>{data.tenant.name}</div>
            </div>
            <div>
              <div className="font-medium">Destinataire</div>
              {data.client.account_name && <div>{data.client.account_name}</div>}
              {data.client.contact_name && <div className="text-muted-foreground">{data.client.contact_name}</div>}
              {data.client.contact_email && <div className="text-muted-foreground">{data.client.contact_email}</div>}
              {data.client.billing_address && (
                <div className="mt-1 text-muted-foreground">
                  {data.client.billing_address}
                  {data.client.billing_postal_code && (
                    <>
                      <br />
                      {data.client.billing_postal_code} {data.client.billing_city}
                    </>
                  )}
                </div>
              )}
            </div>
            <div>
              <div className="font-medium">Date d'émission</div>
              <div>{formatDate(data.issue_date)}</div>
            </div>
            <div>
              <div className="font-medium">Validité</div>
              <div>Jusqu'au {formatDate(data.validity_date)}</div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Détail du devis</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-test="quote-lines-table">
                <thead className="border-b text-left text-muted-foreground">
                  <tr>
                    <th className="py-2 pr-2">Prestation</th>
                    <th className="py-2 pr-2 text-right">Qté</th>
                    <th className="py-2 pr-2 text-right">Prix unitaire</th>
                    <th className="py-2 text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {data.lines.map((line, idx) => (
                    <tr key={idx} className="border-b last:border-0">
                      <td className="py-3 pr-2">
                        <div className="font-medium">{line.description}</div>
                        {line.detailed_description && (
                          <div className="text-xs text-muted-foreground">{line.detailed_description}</div>
                        )}
                      </td>
                      <td className="py-3 pr-2 text-right">
                        {formatQuantity(line.quantity)}
                        {line.unit ? ` ${line.unit}` : ""}
                      </td>
                      <td className="py-3 pr-2 text-right">{formatMoney(line.unit_price, data.currency)}</td>
                      <td className="py-3 text-right font-medium">{formatMoney(line.total_amount, data.currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-4 flex justify-end">
              <div className="w-full max-w-xs space-y-1 text-sm">
                <Row label="Sous-total HT" value={formatMoney(data.subtotal_amount, data.currency)} />
                {parseFloat(String(data.discount_amount)) > 0 && (
                  <Row label="Remise" value={`- ${formatMoney(data.discount_amount, data.currency)}`} />
                )}
                <Row label={`TVA (${data.tax_rate}%)`} value={formatMoney(data.tax_amount, data.currency)} />
                <div className="border-t pt-2">
                  <Row
                    label={<span className="text-base font-semibold">Total TTC</span>}
                    value={<span className="text-base font-semibold" data-test="quote-total">{formatMoney(data.total_amount, data.currency)}</span>}
                  />
                </div>
              </div>
            </div>

            {(data.notes || data.special_conditions) && (
              <div className="mt-6 space-y-3 text-sm">
                {data.notes && (
                  <div>
                    <div className="font-medium">Notes</div>
                    <div className="whitespace-pre-wrap text-muted-foreground">{data.notes}</div>
                  </div>
                )}
                {data.special_conditions && (
                  <div>
                    <div className="font-medium">Conditions particulières</div>
                    <div className="whitespace-pre-wrap text-muted-foreground">{data.special_conditions}</div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="flex flex-wrap items-center gap-3" data-test="public-quote-actions">
          <Button asChild variant="outline" data-test="quote-download-pdf">
            <a href={publicQuotePdfUrl(token)} target="_blank" rel="noreferrer">
              <Download className="mr-2 h-4 w-4" />
              Télécharger le PDF
            </a>
          </Button>
          {canAct && (
            <>
              <Button onClick={() => setAcceptOpen(true)} data-test="quote-accept-btn">
                <CheckCircle2 className="mr-2 h-4 w-4" />
                Accepter le devis
              </Button>
              <Button variant="outline" onClick={() => setRejectOpen(true)} data-test="quote-reject-btn">
                <XCircle className="mr-2 h-4 w-4" />
                Refuser
              </Button>
            </>
          )}
        </div>

        {data.needs_form_url && canAct && (
          <Card className="border-dashed" data-test="needs-cta-section">
            <CardContent className="flex items-start gap-3 p-4">
              <ClipboardList className="mt-1 h-5 w-5 text-primary" />
              <div className="flex-1">
                <div className="font-medium">Aidez-nous à calibrer la formation</div>
                <p className="mt-1 text-sm text-muted-foreground">
                  Quelques minutes pour partager vos attentes et le niveau actuel des participants — le programme sera ajusté en conséquence (Qualiopi indicateur 4).
                </p>
              </div>
              <Button asChild variant="default" data-test="quote-needs-cta">
                <Link to={`/q/${token}/needs`}>Répondre au formulaire</Link>
              </Button>
            </CardContent>
          </Card>
        )}

        <footer className="border-t pt-4 text-xs text-muted-foreground">
          Organisme de formation référencé Qualiopi. Pour toute question, contactez votre interlocuteur dédié.
        </footer>

        <AcceptDialog
          open={acceptOpen}
          onOpenChange={setAcceptOpen}
          quote={data}
          onAccepted={() => {
            setAcceptOpen(false)
            void refetch()
            toast({ title: "Devis accepté", description: "Merci, votre acceptation a bien été enregistrée." })
          }}
        />
        <RejectDialog
          open={rejectOpen}
          onOpenChange={setRejectOpen}
          onRejected={() => {
            setRejectOpen(false)
            void refetch()
            toast({ title: "Devis refusé", description: "Votre refus a bien été enregistré." })
          }}
          token={token}
        />
      </div>
    </PublicShell>
  )
}

function PublicShell({
  tenantName,
  tenantLogo,
  children,
}: {
  tenantName?: string
  tenantLogo?: string | null
  children: React.ReactNode
}) {
  return (
    <div className="min-h-screen bg-muted/30">
      <header className="border-b bg-white">
        <div className="mx-auto flex max-w-3xl items-center gap-3 p-4">
          {tenantLogo && <img src={tenantLogo} alt={tenantName || "Organisme"} className="h-10 w-auto" />}
          <div className="flex items-center gap-2 text-base font-medium">
            <FileText className="h-5 w-5 text-primary" />
            {tenantName || "Qualneo"}
          </div>
        </div>
      </header>
      <main>{children}</main>
    </div>
  )
}

function Row({ label, value }: { label: React.ReactNode; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between">
      <div className="text-muted-foreground">{label}</div>
      <div>{value}</div>
    </div>
  )
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="mx-auto mt-12 max-w-md p-8 text-center">
      <AlertTriangle className="mx-auto h-10 w-10 text-orange-500" />
      <h2 className="mt-3 text-lg font-semibold">Lien indisponible</h2>
      <p className="mt-2 text-sm text-muted-foreground">{message}</p>
    </div>
  )
}

function AcceptDialog({
  open,
  onOpenChange,
  quote,
  onAccepted,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  quote: PublicQuoteResponse
  onAccepted: () => void
}) {
  const { token } = useParams<{ token: string }>()
  const { toast } = useToast()
  const [name, setName] = useState(quote.client.contact_name || "")
  const [email, setEmail] = useState(quote.client.contact_email || "")
  const [cgu, setCgu] = useState(false)

  const mutation = useMutation({
    mutationFn: () =>
      acceptPublicQuote(token!, { name: name.trim(), email: email.trim(), cgu_accepted: cgu }),
    onSuccess: onAccepted,
    onError: (e: Error) => toast({ variant: "destructive", title: "Erreur", description: e.message }),
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-test="accept-dialog">
        <DialogHeader>
          <DialogTitle>Confirmer l'acceptation du devis</DialogTitle>
          <DialogDescription>
            Cette action est définitive. Merci de confirmer votre identité et votre accord pour les conditions du devis.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700" data-test="accept-legal-notice">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" />
            <p>
              En cliquant « Accepter ce devis », vous signez électroniquement ce devis (art. 1366 Code civil). Votre identité, adresse IP et horodatage sont enregistrés comme preuve de votre consentement.
            </p>
          </div>
          <div>
            <Label htmlFor="accept-name">Nom complet</Label>
            <Input
              id="accept-name"
              data-test="accept-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Prénom Nom"
            />
          </div>
          <div>
            <Label htmlFor="accept-email">Email</Label>
            <Input
              id="accept-email"
              type="email"
              data-test="accept-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="vous@exemple.fr"
            />
          </div>
          <div className="flex items-start gap-2">
            <Checkbox
              id="accept-cgu"
              data-test="accept-cgu"
              checked={cgu}
              onCheckedChange={(v) => setCgu(v === true)}
            />
            <Label htmlFor="accept-cgu" className="text-sm font-normal">
              J'accepte les conditions générales et les termes du devis.
            </Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button
            data-test="accept-submit"
            onClick={() => mutation.mutate()}
            disabled={!cgu || !name.trim() || !email.trim() || mutation.isPending}
          >
            {mutation.isPending ? "Validation…" : "Confirmer l'acceptation"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function RejectDialog({
  open,
  onOpenChange,
  onRejected,
  token,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  onRejected: () => void
  token: string
}) {
  const { toast } = useToast()
  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [reason, setReason] = useState("")

  const mutation = useMutation({
    mutationFn: () =>
      rejectPublicQuote(token, {
        name: name.trim(),
        email: email.trim(),
        reason: reason.trim() || undefined,
      }),
    onSuccess: onRejected,
    onError: (e: Error) => toast({ variant: "destructive", title: "Erreur", description: e.message }),
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-test="reject-dialog">
        <DialogHeader>
          <DialogTitle>Confirmer le refus du devis</DialogTitle>
          <DialogDescription>
            Vous pouvez préciser la raison de votre refus (facultatif) afin d'aider l'organisme à proposer une alternative.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="reject-name">Nom complet</Label>
            <Input
              id="reject-name"
              data-test="reject-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="reject-email">Email</Label>
            <Input
              id="reject-email"
              type="email"
              data-test="reject-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="reject-reason">Raison (facultatif)</Label>
            <Textarea
              id="reject-reason"
              data-test="reject-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button
            data-test="reject-submit"
            variant="destructive"
            onClick={() => mutation.mutate()}
            disabled={!name.trim() || !email.trim() || mutation.isPending}
          >
            {mutation.isPending ? "Validation…" : "Confirmer le refus"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
